import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { getClientId } from "./clientId";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ScanBarcode, Search, Trash2, Plus, Minus, User, Pause, PlayCircle,
  Wallet, CreditCard, Smartphone, Printer, RefreshCw, X,
} from "lucide-react";

const PAY_MODES = [
  { key: "cash", label: "Cash", icon: Wallet },
  { key: "card", label: "Card", icon: CreditCard },
  { key: "upi",  label: "UPI",  icon: Smartphone },
];

export default function POSScreen() {
  const { terminal, register, refreshRegister } = useOutletContext();
  const clientId = getClientId();
  const registerBlocked = !terminal || !register || register.locked_by_other;
  const blockReason = !terminal
    ? "Pick a terminal from the top bar to start billing."
    : !register
      ? "Cash Register is closed. Open the register before selecting products for billing."
      : register.locked_by_other
        ? `Terminal is locked to another workstation (${register.opened_by_name || "unknown"}). Close it there to bill here.`
        : null;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const searchTimer = useRef(null);
  const searchInput = useRef(null);

  const [cart, setCart] = useState([]);        // [{product_id, product_name, product_sku, hsn_code, qty, rate, discount_pct, discount_amount, tax_pct, stock_qty}]
  const [customer, setCustomer] = useState(null);   // {id, name, mobile}
  const [billDiscPct, setBillDiscPct] = useState(0);
  const [billDiscAmt, setBillDiscAmt] = useState(0);
  const [payments, setPayments] = useState([{ mode: "cash", amount: 0, reference: "" }]);
  const [payOpen, setPayOpen] = useState(false);
  const [returnMode, setReturnMode] = useState(false);
  const [returnOfBillId, setReturnOfBillId] = useState(null);
  const [holds, setHolds] = useState([]);
  const [holdsOpen, setHoldsOpen] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [custMobile, setCustMobile] = useState("");
  const [custName, setCustName] = useState("");

  // Product search — debounced. Guarded by an open register.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || registerBlocked) { setResults([]); return; }
    searchTimer.current = setTimeout(() => {
      api.get("/pos/products/search", { params: { q } })
        .then((r) => setResults(r.data || []))
        .catch(() => setResults([]));
    }, 250);
  }, [q, registerBlocked]);

  const loadHolds = useCallback(() => {
    if (!terminal) return;
    api.get("/pos/holds", { params: { terminal_id: terminal.id } })
      .then((r) => setHolds(r.data || []));
  }, [terminal]);
  useEffect(() => { loadHolds(); }, [loadHolds]);

  // Barcode scan mode — the Search input captures typed digits fast, if it
  // gets a full CODE128-length match in <500ms we treat as scan.
  const onSearchKey = async (e) => {
    if (registerBlocked) return;
    if (e.key === "Enter" && q) {
      // Direct barcode match: single result → auto-add
      const res = await api.get("/pos/products/search", { params: { barcode: q } });
      const items = res.data || [];
      if (items.length === 1) { addToCart(items[0]); setQ(""); }
      else if (items.length > 1) setResults(items);
      else toast.error("No product for that code");
    }
  };

  const addToCart = (p) => {
    setCart((c) => {
      const idx = c.findIndex((x) => x.product_id === p.id);
      if (idx >= 0) {
        const copy = c.slice();
        copy[idx] = { ...copy[idx], qty: (copy[idx].qty || 0) + 1 };
        return copy;
      }
      return [...c, {
        product_id: p.id, product_name: p.name, product_sku: p.sku || p.code, hsn_code: p.hsn_code,
        qty: 1, rate: Number(p.rate) || 0, discount_pct: 0, discount_amount: 0, tax_pct: Number(p.tax_pct) || 0,
        stock_qty: p.stock_qty,
      }];
    });
  };

  const setLine = (idx, patch) => setCart((c) => c.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  // Totals live
  const totals = useMemo(() => {
    let subtotal = 0, taxTotal = 0, lineDisc = 0;
    const taxSummary = {};
    cart.forEach((l) => {
      const gross = (l.qty || 0) * (l.rate || 0);
      const d = gross * ((l.discount_pct || 0) / 100) + (Number(l.discount_amount) || 0);
      const net = gross - d;
      subtotal += net; lineDisc += d;
      const t = net * ((l.tax_pct || 0) / 100);
      taxTotal += t;
      const key = `${l.tax_pct || 0}%`;
      taxSummary[key] = (taxSummary[key] || 0) + t;
    });
    let billDisc = subtotal * ((Number(billDiscPct) || 0) / 100) + (Number(billDiscAmt) || 0);
    if (billDisc > subtotal) billDisc = subtotal;
    const taxable = subtotal - billDisc;
    if (billDisc > 0 && subtotal > 0) {
      const factor = taxable / subtotal;
      taxTotal *= factor;
      Object.keys(taxSummary).forEach((k) => { taxSummary[k] *= factor; });
    }
    const grand = taxable + taxTotal;
    return { subtotal, lineDisc, billDisc, taxable, taxTotal, taxSummary, grand };
  }, [cart, billDiscPct, billDiscAmt]);

  const canProceed = cart.length > 0 && !registerBlocked;

  // Auto-fill first payment with grand total
  const openPay = () => {
    if (!canProceed) return toast.error("Add items + open register + pick a terminal");
    setPayments([{ mode: "cash", amount: Number(totals.grand.toFixed(2)), reference: "" }]);
    setPayOpen(true);
  };

  const paidTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = Number((totals.grand - paidTotal).toFixed(2));

  const finishBill = async () => {
    if (Math.abs(balance) > 0.05) return toast.error(`Payment mismatch — balance ${balance}`);
    try {
      const res = await api.post("/pos/bills", {
        terminal_id: terminal.id,
        register_id: register.id,
        client_id: clientId,
        customer_id: customer?.id,
        customer_name: customer?.name || "Guest",
        customer_mobile: customer?.mobile,
        lines: cart.map((l) => ({
          product_id: l.product_id, product_name: l.product_name, product_sku: l.product_sku, hsn_code: l.hsn_code,
          qty: l.qty, rate: l.rate, discount_pct: Number(l.discount_pct) || 0,
          discount_amount: Number(l.discount_amount) || 0, tax_pct: Number(l.tax_pct) || 0,
        })),
        payments: payments.filter((p) => Number(p.amount) > 0),
        invoice_discount_pct: Number(billDiscPct) || 0,
        invoice_discount_amount: Number(billDiscAmt) || 0,
        return_of_bill_id: returnMode ? returnOfBillId : null,
      });
      toast.success(`Bill ${res.data.bill_no} saved`);
      // Open print window
      window.open(`/pos/bills/${res.data.id}/print`, "_blank", "noopener,width=380,height=700");
      resetAll();
      refreshRegister?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const resetAll = () => {
    setCart([]); setCustomer(null); setBillDiscPct(0); setBillDiscAmt(0);
    setPayments([{ mode: "cash", amount: 0, reference: "" }]); setPayOpen(false);
    setReturnMode(false); setReturnOfBillId(null); setQ(""); setResults([]);
  };

  const holdBill = async () => {
    if (!cart.length) return toast.error("Nothing to hold");
    try {
      await api.post("/pos/holds", {
        terminal_id: terminal.id, register_id: register?.id || "",
        customer_id: customer?.id, customer_name: customer?.name, customer_mobile: customer?.mobile,
        lines: cart, invoice_discount_pct: Number(billDiscPct) || 0, invoice_discount_amount: Number(billDiscAmt) || 0,
      });
      toast.success("Bill held");
      resetAll(); loadHolds();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hold failed"); }
  };

  const resumeHold = async (h) => {
    setCart(h.lines || []);
    setCustomer(h.customer_id ? { id: h.customer_id, name: h.customer_name, mobile: h.customer_mobile } : null);
    setBillDiscPct(h.invoice_discount_pct || 0);
    setBillDiscAmt(h.invoice_discount_amount || 0);
    setHoldsOpen(false);
    await api.post(`/pos/holds/${h.id}/consume`);
    loadHolds();
  };

  const lookupCust = async () => {
    const m = custMobile.trim().replace(/[\s-]/g, "");
    if (!m) return;
    const r = await api.get("/pos/customers/lookup", { params: { mobile: m } });
    if (r.data) { setCustomer(r.data); setCustName(r.data.name); toast.success("Customer loaded"); }
  };
  const saveCust = async () => {
    if (!custMobile.trim()) return toast.error("Mobile required");
    const r = await api.post("/pos/customers/upsert", { mobile: custMobile, name: custName });
    setCustomer(r.data);
    toast.success("Customer set");
    setCustOpen(false);
  };

  const startReturn = async () => {
    const bn = window.prompt("Enter bill number to return against:");
    if (!bn) return;
    try {
      const r = await api.get(`/pos/bills/${bn.trim()}`);
      const b = r.data;
      setReturnMode(true); setReturnOfBillId(b.id);
      setCart((b.lines || []).map((l) => ({ ...l, qty: Math.abs(l.qty) })));
      setCustomer(b.customer_id ? { id: b.customer_id, name: b.customer_name, mobile: b.customer_mobile } : null);
      toast.info(`Returning bill ${b.bill_no} — adjust quantities, then Refund`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bill not found"); }
  };

  return (
    <div className="p-4 grid grid-cols-12 gap-4 h-full">
      {blockReason && (
        <div className="col-span-12 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 flex items-center gap-2" data-testid="pos-blocked-banner">
          <span className="h-2 w-2 rounded-full bg-amber-500" />
          <span className="font-semibold">{blockReason}</span>
          {!terminal && <span className="ml-auto text-xs">↑ Use the terminal dropdown in the top bar.</span>}
          {terminal && !register && <a href="/pos/register" className="ml-auto text-xs underline font-semibold">Open register →</a>}
        </div>
      )}
      {/* LEFT: cart */}
      <section className="col-span-8 bg-white rounded-lg shadow-sm border border-slate-200 flex flex-col overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <ScanBarcode className="h-5 w-5 text-red-600" />
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input ref={searchInput} autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
              placeholder={registerBlocked ? "Register closed — billing disabled" : "Scan barcode or type product name / SKU… (Enter to add)"}
              disabled={registerBlocked}
              className="pl-8 h-10 font-mono disabled:bg-slate-100 disabled:cursor-not-allowed" data-testid="pos-search" />
          </div>
          <Button variant="outline" onClick={() => setCustOpen(true)} disabled={registerBlocked} data-testid="pos-cust-btn">
            <User className="h-4 w-4 mr-1" /> {customer ? `${customer.name}${customer.mobile ? " · " + customer.mobile : ""}` : "Guest"}
          </Button>
          <Button variant="outline" onClick={holdBill} disabled={!cart.length} data-testid="pos-hold"><Pause className="h-4 w-4 mr-1" />Hold</Button>
          <Button variant="outline" onClick={() => setHoldsOpen(true)} data-testid="pos-resume"><PlayCircle className="h-4 w-4 mr-1" />Resume ({holds.length})</Button>
          <Button variant="outline" onClick={startReturn} data-testid="pos-return"><RefreshCw className="h-4 w-4 mr-1" />Return</Button>
        </div>

        {/* Search results dropdown */}
        {results.length > 0 && (
          <div className="border-b border-slate-100 bg-slate-50 max-h-56 overflow-auto">
            {results.map((p) => (
              <button key={p.id} onClick={() => { addToCart(p); setQ(""); setResults([]); searchInput.current?.focus(); }}
                className="w-full text-left px-3 py-1.5 hover:bg-red-50 flex items-center gap-3 text-sm"
                data-testid={`pos-search-row-${p.id}`}>
                <span className="flex-1 truncate"><b>{p.name}</b> · <span className="font-mono text-xs text-slate-500">{p.sku || p.code}</span></span>
                <span className="text-xs">Stock: <b className={p.stock_qty > 0 ? "text-emerald-700" : "text-red-600"}>{p.stock_qty}</b></span>
                <span className="text-sm font-semibold tabular-nums">₹{Number(p.rate).toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}

        {/* Cart */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 w-32 text-center">Qty</th>
                <th className="px-3 py-2 w-24 text-right">Rate</th>
                <th className="px-3 py-2 w-20 text-right">Disc%</th>
                <th className="px-3 py-2 w-20 text-right">Tax%</th>
                <th className="px-3 py-2 w-24 text-right">Amount</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cart.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-14 text-center text-slate-400">Scan or search to add products.</td></tr>
              )}
              {cart.map((l, i) => {
                const gross = l.qty * l.rate;
                const disc = gross * (l.discount_pct / 100) + (Number(l.discount_amount) || 0);
                const net = gross - disc;
                const tax = net * (l.tax_pct / 100);
                return (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`pos-line-${i}`}>
                    <td className="px-3 py-2"><div className="font-medium">{l.product_name}</div><div className="text-[11px] font-mono text-slate-500">{l.product_sku}</div></td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-0">
                        <button onClick={() => setLine(i, { qty: Math.max(0.01, (l.qty || 0) - 1) })} className="h-7 w-7 rounded-l bg-slate-100 hover:bg-slate-200"><Minus className="h-3 w-3 mx-auto" /></button>
                        <input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} className="h-7 w-14 text-center text-sm border-y border-slate-200 tabular-nums" data-testid={`pos-qty-${i}`} />
                        <button onClick={() => setLine(i, { qty: (l.qty || 0) + 1 })} className="h-7 w-7 rounded-r bg-slate-100 hover:bg-slate-200"><Plus className="h-3 w-3 mx-auto" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right"><input type="number" value={l.rate} onChange={(e) => setLine(i, { rate: Number(e.target.value) })} className="h-7 w-20 text-right border border-slate-200 rounded text-sm tabular-nums px-1" /></td>
                    <td className="px-3 py-2 text-right"><input type="number" value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: Number(e.target.value) })} className="h-7 w-16 text-right border border-slate-200 rounded text-sm tabular-nums px-1" /></td>
                    <td className="px-3 py-2 text-right"><input type="number" value={l.tax_pct} onChange={(e) => setLine(i, { tax_pct: Number(e.target.value) })} className="h-7 w-16 text-right border border-slate-200 rounded text-sm tabular-nums px-1" /></td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">₹{(net + tax).toFixed(2)}</td>
                    <td className="px-3 py-2 text-center"><button onClick={() => removeLine(i)} className="h-7 w-7 rounded hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-500 mx-auto" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* RIGHT: summary + pay */}
      <aside className="col-span-4 bg-white rounded-lg shadow-sm border border-slate-200 flex flex-col overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          {returnMode && <div className="mb-2 text-[11px] uppercase tracking-widest font-semibold bg-red-50 text-red-700 border border-red-200 rounded px-2 py-1 flex items-center justify-between">Return / Refund <button onClick={resetAll}><X className="h-3 w-3" /></button></div>}
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bill Summary</div>
          <div className="mt-3 space-y-1.5 text-sm">
            <Row k="Sub-total" v={totals.subtotal} />
            <Row k="Line Discount" v={totals.lineDisc} tone="text-red-600" prefix="−" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 flex-1">Bill Discount %</span>
              <input type="number" value={billDiscPct} onChange={(e) => setBillDiscPct(e.target.value)} className="h-7 w-20 text-right border border-slate-200 rounded text-sm px-1" data-testid="pos-bill-disc-pct" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 flex-1">Bill Discount ₹</span>
              <input type="number" value={billDiscAmt} onChange={(e) => setBillDiscAmt(e.target.value)} className="h-7 w-20 text-right border border-slate-200 rounded text-sm px-1" data-testid="pos-bill-disc-amt" />
            </div>
            <Row k="Bill Discount" v={totals.billDisc} tone="text-red-600" prefix="−" />
            <Row k="Taxable" v={totals.taxable} />
            {Object.entries(totals.taxSummary).map(([k, v]) => <Row key={k} k={`Tax ${k}`} v={v} small />)}
            <Row k="Tax total" v={totals.taxTotal} />
            <div className="border-t border-slate-200 pt-2 mt-2 flex items-baseline">
              <span className="text-xs uppercase tracking-widest text-slate-500 flex-1">Grand Total</span>
              <span className="text-2xl font-bold tabular-nums text-slate-900" data-testid="pos-grand">₹{totals.grand.toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="p-3 border-t border-slate-100 mt-auto space-y-2">
          <Button onClick={openPay} disabled={!canProceed} className="w-full h-12 bg-red-600 hover:bg-red-700 text-lg" data-testid="pos-checkout">
            {returnMode ? "Process Refund" : "Charge · ₹" + totals.grand.toFixed(2)}
          </Button>
          <Button variant="outline" onClick={resetAll} className="w-full" data-testid="pos-clear">Clear</Button>
        </div>
      </aside>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{returnMode ? "Refund" : "Take Payment"} — ₹{totals.grand.toFixed(2)}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {payments.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={p.mode} onChange={(e) => setPayments((arr) => arr.map((x, ii) => ii === i ? { ...x, mode: e.target.value } : x))} className="h-9 rounded-md border border-slate-200 text-sm px-2 w-28" data-testid={`pos-pay-mode-${i}`}>
                  {PAY_MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                  <option value="wallet">Wallet</option>
                  <option value="other">Other</option>
                </select>
                <Input type="number" value={p.amount} onChange={(e) => setPayments((arr) => arr.map((x, ii) => ii === i ? { ...x, amount: Number(e.target.value) } : x))} className="text-right tabular-nums" data-testid={`pos-pay-amt-${i}`} />
                <Input value={p.reference} onChange={(e) => setPayments((arr) => arr.map((x, ii) => ii === i ? { ...x, reference: e.target.value } : x))} placeholder="Ref#" className="w-32" />
                {payments.length > 1 && <button onClick={() => setPayments((arr) => arr.filter((_, ii) => ii !== i))} className="text-red-500 h-9 w-9"><Trash2 className="h-4 w-4 mx-auto" /></button>}
              </div>
            ))}
            <Button variant="outline" onClick={() => setPayments((arr) => [...arr, { mode: "card", amount: Math.max(0, balance), reference: "" }])} className="w-full h-8">+ Add Payment</Button>
            <div className="pt-2 border-t border-slate-200 flex items-center text-sm">
              <span className="text-slate-500 flex-1">Balance</span>
              <span className={`font-semibold ${Math.abs(balance) < 0.05 ? "text-emerald-700" : "text-red-600"} tabular-nums`}>₹{balance.toFixed(2)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={finishBill} className="bg-red-600 hover:bg-red-700" data-testid="pos-finish"><Printer className="h-4 w-4 mr-1" />Save &amp; Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Holds dialog */}
      <Dialog open={holdsOpen} onOpenChange={setHoldsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Held Bills</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-auto divide-y divide-slate-100">
            {holds.length === 0 && <div className="p-6 text-center text-slate-400">No held bills.</div>}
            {holds.map((h) => (
              <div key={h.id} className="p-2 flex items-center gap-3" data-testid={`hold-row-${h.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{h.hold_no} · {h.customer_name || "Guest"}</div>
                  <div className="text-xs text-slate-500">{(h.lines || []).length} items · ₹{Number(h.totals?.grand_total || 0).toFixed(2)}</div>
                </div>
                <Button size="sm" onClick={() => resumeHold(h)} className="bg-red-600 hover:bg-red-700">Resume</Button>
                <button onClick={async () => { await api.delete(`/pos/holds/${h.id}`); loadHolds(); }} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer picker */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Customer</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input value={custMobile} onChange={(e) => setCustMobile(e.target.value)} placeholder="Mobile" autoFocus data-testid="cust-mobile" />
              <Button variant="outline" onClick={lookupCust}>Look up</Button>
            </div>
            <Input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="Name (for new)" data-testid="cust-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCustomer(null); setCustOpen(false); }}>Set as Guest</Button>
            <Button onClick={saveCust} className="bg-red-600 hover:bg-red-700" data-testid="cust-save">Save &amp; Use</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v, tone = "", prefix = "", small = false }) {
  return (
    <div className={`flex items-baseline ${small ? "text-xs" : ""}`}>
      <span className="text-xs text-slate-500 flex-1">{k}</span>
      <span className={`tabular-nums ${tone}`}>{prefix}₹{Number(v || 0).toFixed(2)}</span>
    </div>
  );
}
